import React from "react";

import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import { useModal } from "@editor/hooks/useModal";
import { setDebugPanelVisibility } from "@editor/reducers/core-reducer";
import { useEditorDispatch } from "@editor/store";

import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  Bug,
  EyeOff,
  Paintbrush,
  Settings,
  Settings2,
  Trash2,
} from "lucide-react";

import { useEditorStoreActions, usePanelView } from "../../store/editorStore";
import { useCurrentFile } from "../../utils/useCurrentFile";

const PageSettingsMenu = () => {
  const [isSettingsComboboxOpen, setIsSettingsComboboxOpen] =
    React.useState(false);

  const dispatch = useEditorDispatch();
  const isDebugMode = useIsDebugMode();
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const { openModal } = useModal();
  const currentFile = useCurrentFile();
  const isMobile = useIsMobile();

  const isPagePublished = Boolean(currentFile?.lastPublishedFileVersionId);

  return (
    <Combobox.Root
      open={isSettingsComboboxOpen}
      onOpenChange={setIsSettingsComboboxOpen}
      onChange={(value: string) => {
        if (value === "design-library") {
          setPanelView("designLibrary");
        } else if (value === "page-settings") {
          setPanelView("pageSettings");
        } else if (value === "unpublish-page") {
          if (currentFile) {
            openModal({
              type: "unpublishModal",
              props: {
                type: "page",
                page: currentFile,
              },
            });
          }
        } else if (value === "delete-page") {
          // eslint-disable-next-line unicorn/no-lonely-if -- it's more intuitive this way
          if (currentFile) {
            openModal({
              type: "deletePageModal",
              props: {
                page: currentFile,
              },
            });
          }
        } else if (value === "open-debug-panel" && isDebugMode) {
          dispatch(setDebugPanelVisibility(true));
        }
        setIsSettingsComboboxOpen(false);
      }}
      options={[
        {
          value: "page-settings",
          label: (
            <MenuItem
              variant="default"
              selected={panelView === "pageSettings"}
              startEnhancer={<Settings size={16} />}
            >
              Page Settings
            </MenuItem>
          ),
          displayValue: "Page Settings",
        },
        {
          value: "design-library",
          label: (
            <MenuItem
              variant="default"
              selected={panelView === "designLibrary"}
              startEnhancer={<Paintbrush size={16} />}
            >
              Design Library
            </MenuItem>
          ),
          displayValue: "Design Library",
        },
        ...(isMobile && isDebugMode
          ? [
              {
                value: "open-debug-panel",
                label: (
                  <MenuItem variant="default" startEnhancer={<Bug size={16} />}>
                    Open Debug Panel
                  </MenuItem>
                ),
                displayValue: "Open Debug Panel",
              },
            ]
          : []),
        ...(isPagePublished
          ? [
              {
                value: "unpublish-page",
                label: (
                  <MenuItem
                    variant="default"
                    startEnhancer={<EyeOff size={16} />}
                  >
                    Unpublish Page
                  </MenuItem>
                ),
                displayValue: "Unpublish Page",
              },
            ]
          : []),
        {
          value: "delete-page",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<Trash2 className="text-danger" size={16} />}
              UNSAFE_className="text-danger"
              disabled={isPagePublished}
              toolTipText={
                isPagePublished ? "Unpublish this page to delete" : undefined
              }
            >
              Delete Page
            </MenuItem>
          ),
          displayValue: "Delete Page",
        },
      ]}
    >
      <Combobox.Trigger>
        <IconButton
          variant="tertiary"
          size="base"
          UNSAFE_className={twMerge(
            (panelView === "designLibrary" || panelView === "pageSettings") &&
              "bg-primary/10 hover:bg-primary/10",
          )}
          icon={
            <Settings2
              className={twMerge(
                "text-muted",
                (panelView === "designLibrary" ||
                  panelView === "pageSettings") &&
                  "text-primary",
              )}
              size={16}
            />
          }
        />
      </Combobox.Trigger>
      <Combobox.Popover layoutClassName="w-48">
        <Combobox.Content />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default PageSettingsMenu;
